import { fetchOpenAIText } from "../utils/openaiClient";

// Ensure the exercise carries the exact source text the teacher typed as
// its `passage` attribute. Trusting the model to echo it back is risky —
// it silently paraphrases, translates, or shortens the input. Overwriting
// with the original guarantees the "Read the text" panel shows what the
// teacher expects.
const withABCDPassage = (data, source) => {
  if (!data || typeof data !== 'object') return data;
  return { ...data, passage: source || '' };
};

// Aggressive question-text normalization for dedup: kills punctuation,
// case, extra whitespace, and pad-tags like "(variant N)". Two questions
// hash to the same key if they'd read identically to a student.
const normalizeQuestionKey = (q) => {
  return ((q && q.question) || '')
    .toString()
    .toLowerCase()
    .replace(/\s*\(variant\s+\d+\)\s*$/i, '')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
};
const dedupABCDQuestions = (questions) => {
  if (!Array.isArray(questions)) return [];
  const seen = new Set();
  return questions.filter(q => {
    const key = normalizeQuestionKey(q);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// Randomise which option position (a/b/c/d) holds the correct answer for each
// question and reassign the letter ids to match the new order. The model has
// a documented bias toward placing the correct answer in the position shown
// by the JSON example; even after diversifying the example, some questions
// still cluster. We shuffle in code so trust is guaranteed.
const shuffleABCDQuestions = (data) => {
  if (!data || !Array.isArray(data.questions)) return data;
  const shuffled = data.questions.map(q => {
    const opts = Array.isArray(q.options) ? q.options.slice() : [];
    if (opts.length < 2) return q;
    for (let i = opts.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [opts[i], opts[j]] = [opts[j], opts[i]];
    }
    const letters = ['a','b','c','d'];
    // Ensure exactly one option is marked correct; if the model returned
    // zero or many, fall back to the first as correct.
    let correctIdx = opts.findIndex(o => o?.isCorrect === true);
    const anyCorrect = opts.some(o => o?.isCorrect === true);
    if (!anyCorrect) correctIdx = 0;
    const nextOpts = opts.map((o, i) => ({
      ...o,
      id: letters[i] || String.fromCharCode(97 + i),
      isCorrect: i === correctIdx,
    }));
    return { ...q, options: nextOpts };
  });
  return { ...data, questions: shuffled };
};

export function useABCDGeneratorLogic({
  abcdInput, setABCDInput, abcdLevel, abcdQuestionCount,
  setABCDLoading, setGeneratedABCD, generatedABCD,
  currentTab, onInsertToBoard, activeEditor,
  setShowABCDGenerator, setSelectedTemplate,
}) {
  const handleABCDGenerate = async () => {
    if (!abcdInput.trim()) return;

    setABCDLoading(true);
    try {
      const prompt = `You are an experienced ESL/EFL assessment writer. Build an ABCD reading-comprehension exercise from the text below. Exactly ${abcdQuestionCount} questions, 4 options each (a, b, c, d), exactly one correct option per question.

TARGET CEFR LEVEL: ${abcdLevel} — match strictly (see the calibration table).

CEFR CALIBRATION (do BOTH):
- A1: 5–10 words per question; only present simple / present continuous; concrete nouns from daily life; distractors are also A1 words. AVOID: modals, passive, phrasal verbs, abstract nouns.
- A2: 8–14 words; simple past + comparatives + basic modals (can, must); everyday context. AVOID: perfect tenses, conditionals, academic vocab.
- B1: 10–18 words; past simple/continuous, present perfect, first conditional; common phrasal verbs allowed. AVOID: subjunctive, inversion, C-level lexis (elaborate, notwithstanding, etc.).
- B2: 12–22 words; all perfect tenses, second/third conditional, passive; abstract themes OK; nuanced distractors that share a real-world confusion (e.g. "affect" vs "effect"). AVOID: rare literary structures.
- C1: 14–26 words; complex clauses, inversion, hedging; academic/journalistic register; distractors that trip up on collocation, register, or connotation.
- C2: 16–30 words; near-native register; distractors that fail only on subtle connotation, idiomaticity, or discourse marker choice.

QUESTION MIX (across the ${abcdQuestionCount} items, aim for a spread):
- Factual: "According to the text, when/where/who…"
- Inferential: "The author implies that…" / "It is likely that…"
- Vocabulary-in-context: quote the target word so the student can find it: "In the passage, the word 'breathtaking' is closest in meaning to…". NEVER reference line numbers, paragraph numbers, or positions ("in line 5", "in paragraph 2", "in the first sentence") — the student reads flowing text with no such markers, so those references are meaningless and confusing.
- Reference: pronoun / referent identification (only at B2+) — quote the pronoun with a short surrounding phrase in the question, do NOT say "the pronoun in line X".
- Author's purpose or tone (only at B2+)

HARD RULE ON QUESTION UNIQUENESS:
- Every question must ask about a DIFFERENT fact, inference, word, or reference. Do NOT restate the same question with slightly different wording. Do NOT append variants ("variant 2", "again", "similarly"). Do NOT recycle the same target word across items.
- If the source text genuinely does not support ${abcdQuestionCount} distinct, meaningful questions, return FEWER questions rather than padding. Quality > quantity.

DISTRACTOR QUALITY:
- Distractors are common WRONG answers a student at ${abcdLevel} would actually pick — pulled from the same paragraph, or a plausible misreading. Never absurd.
- All 4 options are similar length (within ~40% of each other) and same register.
- No near-duplicates: never list two options that mean the same thing in different words ("was gone" / "had left"), and never two options differing only by contraction ("isn't" / "is not").
- No "All of the above" / "None of the above" / "Both a and c".
- No option quotes the question verbatim.

OUTPUT:
- The "questions" array MUST contain exactly ${abcdQuestionCount} items.
- Randomize which position (a/b/c/d) holds the correct option across the set — do NOT let one letter dominate. (We also shuffle in code, so trust randomness here.)
- SELF-CHECK before returning: read each question, mentally pick the intended correct option, and confirm it is the ONLY one that fits. If two options could be defended, rewrite the distractor.

Return ONLY valid JSON in this exact schema (the example below shows correct at position C on purpose — you must vary the position in your own output). Do NOT echo the source text back — the "passage" field is written by the app; leave it as an empty string. This saves tokens for the questions themselves:
{
  "title": "Exercise Title",
  "level": "${abcdLevel}",
  "passage": "",
  "questions": [
    {
      "question": "According to the passage, what did the researchers discover?",
      "options": [
        {"id": "a", "text": "Distractor pulled from the same paragraph", "isCorrect": false},
        {"id": "b", "text": "A plausible misreading of the main claim", "isCorrect": false},
        {"id": "c", "text": "The actual finding stated in the text", "isCorrect": true},
        {"id": "d", "text": "A related but unsupported inference", "isCorrect": false}
      ]
    }
  ]
}

Text: ${abcdInput}`;

      // maxTokens scales with question count so 10-question requests actually
      // fit. Rough budget: ~140 tokens per question (stem + 4 options + JSON
      // syntax) + ~400 tokens of wrapper/schema/instructions echo. Passage is
      // intentionally NOT echoed by the model (we own it), so budget stays lean.
      const abcdMaxTokens = Math.min(4000, 400 + abcdQuestionCount * 140);
      const content = await fetchOpenAIText({ prompt, temperature: 0.7, maxTokens: abcdMaxTokens });

      if (content) {

        let contentTrimmed = content.trim();
        // Strip markdown code fences if present
        if (contentTrimmed.startsWith('```json')) {
          contentTrimmed = contentTrimmed.replace(/^```json[\r\n]+/, '').replace(/```$/, '').trim();
        } else if (contentTrimmed.startsWith('```')) {
          contentTrimmed = contentTrimmed.replace(/^```[\r\n]+/, '').replace(/```$/, '').trim();
        }
        // Try to extract JSON object if extra text is present
        const objMatch = contentTrimmed.match(/\{[\s\S]*\}/);
        if (objMatch) {
          contentTrimmed = objMatch[0];
        }


        try {
          let jsonData = JSON.parse(contentTrimmed);
          // Enforce the desired question count
          if (Array.isArray(jsonData.questions)) {
            // Dedup FIRST: models often pad to the requested count by cloning
            // the last question. Collapses near-duplicates regardless of
            // trailing punctuation or "variant N" tags.
            jsonData.questions = dedupABCDQuestions(jsonData.questions);
            if (jsonData.questions.length > abcdQuestionCount) {
              jsonData.questions = jsonData.questions.slice(0, abcdQuestionCount);
            } else if (jsonData.questions.length < abcdQuestionCount) {
              // One targeted retry: ask the model to add the missing questions
              // as NEW, distinct items (not duplicates of what we already have).
              const alreadyHave = jsonData.questions.length;
              const missing = abcdQuestionCount - alreadyHave;
              const existingQs = jsonData.questions.map((q, i) => `${i + 1}. ${q.question || ''}`).join('\n');
              const fixPrompt = `You returned ${alreadyHave} of ${abcdQuestionCount} ABCD questions for the passage below. Add ${missing} MORE questions to reach ${abcdQuestionCount} total.\n\nHard rules:\n- The new questions must be DIFFERENT from the existing ones (different facts, inferences, words, references).\n- Do NOT restate an existing question with new wording, and do NOT use "variant"/"again"/"similarly".\n- Never reference line or paragraph numbers — quote the target word or short phrase directly.\n- CEFR level ${abcdLevel}. Same 4-option (a/b/c/d) format, exactly one correct.\n\nExisting questions (do not repeat these):\n${existingQs}\n\nReturn ONLY valid JSON in the same schema, with the full ${abcdQuestionCount}-item questions array (existing + new).\n\nText:\n${abcdInput}`;
              const fixed = await fetchOpenAIText({ prompt: fixPrompt, temperature: 0.8, maxTokens: 1800 });
              try {
                let fixedText = (fixed || '').trim();
                if (fixedText.startsWith('```json')) {
                  fixedText = fixedText.replace(/^```json[\r\n]+/, '').replace(/```$/, '').trim();
                } else if (fixedText.startsWith('```')) {
                  fixedText = fixedText.replace(/^```[\r\n]+/, '').replace(/```$/, '').trim();
                }
                const fixedParsed = JSON.parse(fixedText);
                if (Array.isArray(fixedParsed.questions)) {
                  // Merge existing + newly-generated, dedup on normalized
                  // question text. This is safer than trusting the retry
                  // to preserve the original questions verbatim (it often
                  // rephrases them, which slips past a naive dedup).
                  const merged = dedupABCDQuestions([
                    ...jsonData.questions,
                    ...fixedParsed.questions,
                  ]);
                  if (merged.length > jsonData.questions.length) {
                    jsonData = { ...jsonData, questions: merged.slice(0, abcdQuestionCount) };
                  }
                }
              } catch (_) {}
              // No cloning-based padding: better to show fewer, distinct questions
              // than to repeat the last one with a "(variant N)" tag, which the
              // teacher instantly reads as broken output.
            }
          }

          setGeneratedABCD(withABCDPassage(shuffleABCDQuestions(jsonData), abcdInput));
        } catch (e) {
          // Attempt a one-shot JSON reformat pass via the model to salvage content
          try {
            const reformatPrompt = `Convert the following text into VALID JSON strictly matching this schema and property names:
{
  "title": string,
  "level": string,
  "questions": [
    {
      "question": string,
      "options": [
        { "id": "a"|"b"|"c"|"d", "text": string, "isCorrect": boolean }
      ]
    }
  ]
}

Rules:
- Return ONLY JSON (no markdown fences, no explanations).
- Ensure exactly one option has isCorrect=true per question.
- If the content includes extra commentary, ignore it.

Content to convert:\n\n${content}`;
            let fixedText = await fetchOpenAIText({ prompt: reformatPrompt, temperature: 0.2, maxTokens: 1500 });
            fixedText = (fixedText || '').trim();
            if (fixedText.startsWith('```json')) {
              fixedText = fixedText.replace(/^```json[\r\n]+/, '').replace(/```$/, '').trim();
            } else if (fixedText.startsWith('```')) {
              fixedText = fixedText.replace(/^```[\r\n]+/, '').replace(/```$/, '').trim();
            }
            const match = fixedText.match(/\{[\s\S]*\}/);
            if (match) fixedText = match[0];
            let parsed = JSON.parse(fixedText);
            // Enforce count
            if (Array.isArray(parsed.questions)) {
              if (parsed.questions.length > abcdQuestionCount) {
                parsed.questions = parsed.questions.slice(0, abcdQuestionCount);
              } else if (parsed.questions.length < abcdQuestionCount && parsed.questions.length > 0) {
                const last = parsed.questions[parsed.questions.length - 1];
                while (parsed.questions.length < abcdQuestionCount) {
                  const idx = parsed.questions.length + 1;
                  const clone = JSON.parse(JSON.stringify(last));
                  clone.question = (clone.question || 'Question') + ` (variant ${idx})`;
                  let correctSet = false;
                  clone.options = (clone.options || []).map((opt, i) => {
                    if (!correctSet && (opt.isCorrect || i === 0)) {
                      correctSet = true;
                      return { ...opt, id: ['a','b','c','d'][i] || 'a', isCorrect: true };
                    }
                    return { ...opt, id: ['a','b','c','d'][i] || 'a', isCorrect: false };
                  });
                  parsed.questions.push(clone);
                }
              }
            }
            setGeneratedABCD(withABCDPassage(shuffleABCDQuestions(parsed), abcdInput));
            return;
          } catch (e2) {
            // Reformat pass failed
          }

          // Final fallback that respects the requested count
          const makeOption = (id, text, isCorrect) => ({ id, text, isCorrect });
          const sampleOptions = [
            makeOption('a', 'Option A', false),
            makeOption('b', 'Option B', true),
            makeOption('c', 'Option C', false),
            makeOption('d', 'Option D', false),
          ];
          const questions = Array.from({ length: abcdQuestionCount }).map((_, i) => ({
            question: `Sample question ${i + 1}?`,
            options: sampleOptions.map(o => ({ ...o })),
          }));
          setGeneratedABCD(withABCDPassage(shuffleABCDQuestions({ title: "ABCD Exercise", level: abcdLevel, questions }), abcdInput));
        }
      }
    } catch (e) {
      // Error in handleABCDGenerate
    } finally {
      setABCDLoading(false);
    }
  };

  const handleABCDInsert = () => {
    if (!generatedABCD) return;
    if (currentTab > 0 && onInsertToBoard) {
      onInsertToBoard(generatedABCD, 'abcd');
      setShowABCDGenerator(false);
      setABCDInput("");
      return;
    }
    if (!activeEditor) return;
    const abcdContent = {
      type: 'abcdExercise',
      attrs: {
        questions: generatedABCD.questions,
        title: generatedABCD.title,
        level: generatedABCD.level,
        // Source text the questions were generated from — surfaced inside
        // the exercise via a collapsible "Read the text" panel.
        passage: generatedABCD.passage || abcdInput,
      }
    };

    activeEditor.chain().focus().insertContent(abcdContent).run();
    setShowABCDGenerator(false);
    setABCDInput("");
  };

  const handleABCDCancel = () => {
    setShowABCDGenerator(false);
    setABCDInput("");
    setGeneratedABCD(null);
    setSelectedTemplate(null);
  };

  return { handleABCDGenerate, handleABCDInsert, handleABCDCancel };
}
